import { Updater, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { getFindProductGridKey } from '../../../../../../../../../services/modules/product-grid/product-grid.keys';
import { useFindProductGrid } from '../../../../../../../../../services/modules/product-grid/product-grid.queries';
import {
  FindProductGridOutput,
  initialGridData,
} from '../../../../../../../../../services/modules/product-grid/product-grid.types';
import { useApplicationContext } from '../../../../../../../../context/ApplicationContext/ApplicationContext';
import { isOfflineStatus } from '../../../../../../../../offline/domain/offlineOrder/offlineOrder.types';
import { UseProductGridOutput } from '../../../../../../interfaces/use-product-grid.interface';
import { getSuggestedGridHandler } from '../../utils/suggestedGrid.utils';
import { UseProductGridProps } from './useProductGrid.interfaces';

export const useProductGrid = ({
  availability,
  productColorId,
  deliveryWindowId,
  orderId,
  orderStatus,
  kitId,
  isOfflineMode = false,
  useDefaultPriceTableWhenOffline = false,
  enabled,
}: UseProductGridProps) => {
  const queryClient = useQueryClient();
  const { team } = useApplicationContext();
  const isInOfflineMode = isOfflineStatus(orderStatus) || isOfflineMode;
  const {
    data: gridData,
    isLoading,
    isFetching,
  } = useFindProductGrid({
    availability,
    productColorId,
    deliveryWindowId,
    orderId,
    isOfflineMode: isInOfflineMode,
    kitId,
    useDefaultPriceTableWhenOffline,
    enabled,
  });

  const grid = gridData?.grid || [];
  const suggestedGrid = gridData?.suggestedGrid || [];
  const suggestedPacks = gridData?.suggestedPacks || [];
  const selectedPacks = gridData?.selectedPacks || [];
  const hasValidPacks = gridData?.hasValidPacks || false;

  const gridQueryKey = useMemo(
    () =>
      getFindProductGridKey({
        availability,
        productColorId,
        deliveryWindowId,
        orderId,
        isOfflineMode: isInOfflineMode,
      }),
    [availability, productColorId, deliveryWindowId, orderId, isInOfflineMode],
  );

  const onChangeGrid = useCallback(
    (fn: (data: FindProductGridOutput) => FindProductGridOutput) => {
      queryClient.setQueryData<FindProductGridOutput>(
        [team?.id, ...gridQueryKey],
        fn as Updater<FindProductGridOutput | undefined, FindProductGridOutput>,
      );
    },
    [gridQueryKey, queryClient, team?.id],
  );

  const resetSelectedPacks = useCallback(() => {
    onChangeGrid((oldData?: FindProductGridOutput) => {
      return oldData
        ? {
            ...oldData,
            selectedPacks: [],
          }
        : initialGridData;
    });
  }, [onChangeGrid]);

  const { showSuggestedGrid, suggestionAchieved } = getSuggestedGridHandler({
    grid,
    suggestedGrid,
    orderStatus: isOfflineStatus(orderStatus) ? undefined : orderStatus,
  });

  const data: UseProductGridOutput = {
    grid,
    suggestedGrid,
    suggestedPacks,
    selectedPacks,
    hasValidPacks,
    isLoading,
    isFetching,
    showSuggestedGrid,
    suggestionAchieved,
    resetSelectedPacks,
  };

  return data;
};
